import type { SessionsCatalogRowResponse } from "@simbelapp/order-sdk";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { PromiseSettledStatusEnum } from "~/utils/enums/common.enums";
import { SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";

/** An intra session books the whole group, an inter date places participants one by one. */
const SESSION_TYPE_LABEL: Record<SessionItemTypeEnum, string> = {
  [SessionItemTypeEnum.GROUP]: "Intra",
  [SessionItemTypeEnum.INDIVIDUAL]: "Inter",
};

/**
 * Columns shared by the "Sessions au plan" and "Dates disponibles" tables.
 * Static tables rendered by SimpleTable: no sorting nor hideable columns, hence a plain constant.
 * No column carries `widthPx`: SimpleTable would switch to `table-layout: fixed`, where
 * `minWidthPx` is ignored and "Lieu" would wrap onto two lines.
 */
const CATALOG_SESSION_HEADERS: IIndexHeader[] = [
  { name: "Début", minWidthPx: 96 },
  { name: "Fin", minWidthPx: 96 },
  { name: "Type", minWidthPx: 88 },
  { name: "Participants", minWidthPx: 100 },
  { name: "Lieu", minWidthPx: 200 },
  { name: "", align: "right" },
];

/** Why a date created from the catalogue stays out of the plan until someone registers on it. */
const NOT_IN_PLAN_TOOLTIP =
  "Quand vous ajoutez une date ici, elle devient sélectionnable par les participants, via l’auto-inscription, " +
  "si elle est activée, ou quand vous leur demandez de choisir une date.\n" +
  "Elle ne sera ajoutée au plan qu’après une inscription.\n" +
  "Pour ajouter des sessions inter directement au plan, rendez-vous dans la vue Sessions.";

/** The two catalogue calls, merged — and whether the admin has to be told one of them failed. */
export interface ICatalogSessionsResult {
  inPlan: SessionsCatalogRowResponse[];
  notInPlan: SessionsCatalogRowResponse[];
  failed: boolean;
}

/**
 * One endpoint failing must not blank out the rows the other returned: the tab would then claim the
 * training has no session at all, and « Dates disponibles » is where the delete action lives — an
 * admin reading an empty tab would re-create a date that already exists.
 */
export function mergeCatalogSessions(
  inPlan: PromiseSettledResult<SessionsCatalogRowResponse[]>,
  notInPlan: PromiseSettledResult<SessionsCatalogRowResponse[]>,
): ICatalogSessionsResult {
  return {
    inPlan: inPlan.status === PromiseSettledStatusEnum.FULFILLED ? inPlan.value : [],
    notInPlan: notInPlan.status === PromiseSettledStatusEnum.FULFILLED ? notInPlan.value : [],
    // One message however many calls failed: two toasts for one reload say nothing more.
    failed:
      inPlan.status === PromiseSettledStatusEnum.REJECTED || notInPlan.status === PromiseSettledStatusEnum.REJECTED,
  };
}

/**
 * An inverted pair creates a session that can never reach its own minimum. Both bounds stay
 * optional — an empty capacity means "no limit" — so a missing one is never an error.
 */
export function isCapacityRangeValid(max: unknown, min: unknown): boolean {
  if (!Number.isFinite(max) || !Number.isFinite(min)) return true;
  return Number(max) >= Number(min);
}

/** Which inscription types the Inscriptions tab leaves enabled on the training. */
export interface IEnabledSessionTypes {
  individual: boolean;
  group: boolean;
}

export const useTrainingCatalogSessions = () => {
  /** An inscription type unchecked on the Inscriptions tab hides its sessions from both tables. */
  function filterByEnabledType(
    sessions: SessionsCatalogRowResponse[],
    enabled: IEnabledSessionTypes,
  ): SessionsCatalogRowResponse[] {
    return sessions.filter((session) =>
      session.sessionItemType === SessionItemTypeEnum.INDIVIDUAL ? enabled.individual : enabled.group,
    );
  }

  function sessionTypeTag(session: SessionsCatalogRowResponse): { value: string; type: ColorsTypeEnum } {
    return { value: SESSION_TYPE_LABEL[session.sessionItemType], type: ColorsTypeEnum.GREY };
  }

  /** "12/10", or the headcount alone when neither the session nor its item caps the seats. */
  function sessionCapacity(session: SessionsCatalogRowResponse): string {
    return session.maxNbAttendees ? `${session.nbAttendees}/${session.maxNbAttendees}` : `${session.nbAttendees}`;
  }

  return {
    catalogSessionHeaders: CATALOG_SESSION_HEADERS,
    notInPlanTooltip: NOT_IN_PLAN_TOOLTIP,
    filterByEnabledType,
    sessionTypeTag,
    sessionCapacity,
  };
};
